/**
 * App mobile.
 *
 * Não é um segundo cliente: consome os mesmos dados já carregados pelo shell
 * desktop e as mesmas telas de dados. O que muda é a navegação (tab bar em vez
 * de sidebar) e a densidade.
 */
import { Icon } from '@iconify/react';
import { useMemo, useState } from 'react';

import { Barra, Botao, Kpi, Ponto, Segmented, Tag, TagComPonto } from '../components/Sga';
import { useAuth } from '../lib/auth';
import {
  hhmm,
  hhmmss,
  iconeDoCanal,
  kindDoContainer,
  kindDoEstado,
  kindDoNivel,
  kindDoStatus,
  n,
  pct,
  rotuloEstado,
  rotuloNivel,
  rotuloStatus,
  segundos,
  identidadeDaConexao,
  telefone,
} from '../lib/format';
import type {
  Conexao,
  Dashboard,
  Fila,
  LogIntegracao,
  PainelAntiban,
  SerieFila,
  Tenant,
} from '../lib/types';

type Aba = 'painel' | 'conexoes' | 'filas' | 'logs';
type FiltroConexao = 'todas' | 'cloud' | 'online' | 'throttling' | 'offline' | 'banidas';
type VisaoFila = 'timeline' | 'grafico' | 'throttling';

const ABAS: { aba: Aba; rotulo: string; icone: string }[] = [
  { aba: 'painel', rotulo: 'Painel', icone: 'mdi:view-dashboard-outline' },
  { aba: 'conexoes', rotulo: 'Conexões', icone: 'mdi:lan-connect' },
  { aba: 'filas', rotulo: 'Filas', icone: 'mdi:format-list-numbered' },
  { aba: 'logs', rotulo: 'Logs', icone: 'mdi:text-box-search-outline' },
];

const CHIPS: { valor: FiltroConexao; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'cloud', rotulo: 'API oficial' },
  { valor: 'online', rotulo: 'Online' },
  { valor: 'throttling', rotulo: 'Throttling' },
  { valor: 'offline', rotulo: 'Offline' },
  { valor: 'banidas', rotulo: 'Banidas' },
];

export function MessengerMobile({
  dashboard,
  conexoes,
  fila,
  serie,
  antiban,
  logs,
  aoVoltarDesktop,
  aoAbrirConexao,
  aoNovaConexao,
}: {
  dashboard: Dashboard | null;
  conexoes: Conexao[];
  fila: Fila | null;
  serie: SerieFila | null;
  antiban: PainelAntiban | null;
  logs: LogIntegracao[];
  aoVoltarDesktop: () => void;
  aoAbrirConexao: (id: string) => void;
  aoNovaConexao: () => void;
}) {
  const { usuario, tenants, tenantAtivo, trocarTenant } = useAuth();
  const [aba, setAba] = useState<Aba>('painel');
  const [filtro, setFiltro] = useState<FiltroConexao>('todas');
  const [visaoFila, setVisaoFila] = useState<VisaoFila>('timeline');
  const [folhaAberta, setFolhaAberta] = useState(false);
  const [busca, setBusca] = useState('');

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return conexoes.filter((c) => {
      if (termo && !`${c.nome} ${c.numero ?? ''} ${c.setor ?? ''}`.toLowerCase().includes(termo)) {
        return false;
      }
      switch (filtro) {
        case 'cloud':
          return c.canal === 'cloud';
        case 'online':
          return c.status === 'online';
        case 'throttling':
          return c.status === 'throttling';
        case 'offline':
          return c.status === 'offline';
        case 'banidas':
          return c.status === 'banido';
        default:
          return true;
      }
    });
  }, [conexoes, filtro, busca]);

  const iniciais = (usuario?.nome ?? 'SGA')
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  const escolherTenant = async (t: Tenant) => {
    setFolhaAberta(false);
    if (t.id !== tenantAtivo?.id) await trocarTenant(t);
  };

  return (
    <div className="mob">
      <header className="mob__header">
        <div className="mob__header-linha">
          {/* Mesma porta de entrada do desktop: a logo abre a arquitetura. */}
          <a
            href="/arquitetura.html"
            target="_blank"
            rel="noopener"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: '#fff', textDecoration: 'none' }}
            title="Ver o diagrama de arquitetura"
          >
            <img src="/logos/sga-logo-branca.svg" alt="SGA" style={{ height: 22 }} />
            <strong style={{ fontSize: 16, fontWeight: 500 }}>Messenger</strong>
          </a>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="app__icone-header"
            style={{ width: 40, height: 40 }}
            onClick={aoVoltarDesktop}
            aria-label="Voltar ao desktop"
          >
            <Icon icon="mdi:monitor" width={19} />
          </button>
          <span className="avatar">{iniciais}</span>
        </div>
        <button type="button" className="mob__chip-tenant" onClick={() => setFolhaAberta(true)}>
          <Icon icon={tenantAtivo?.icone ?? 'mdi:office-building-outline'} width={15} />
          <span style={{ flex: 1, textAlign: 'left' }}>{tenantAtivo?.nome ?? 'Selecione'}</span>
          {tenantAtivo?.conexoes ?? 0} conexões
          <Icon icon="mdi:chevron-down" width={15} />
        </button>
      </header>

      <div className="mob__conteudo">
        {aba === 'painel' && dashboard && (
          <>
            <div className="sga-grid sga-grid--2">
              <Kpi
                icone="mdi:lan-connect"
                rotulo="Conexões"
                valor={`${dashboard.kpis.conexoes_ativas.ok}/${dashboard.kpis.conexoes_ativas.total}`}
              />
              <Kpi
                icone="mdi:message-processing-outline"
                rotulo="Hoje"
                valor={n(dashboard.kpis.mensagens_hoje)}
              />
              <Kpi
                icone="mdi:format-list-numbered"
                rotulo="Fila"
                valor={n(dashboard.kpis.fila_atual)}
                cor="var(--sga-warning)"
              />
              <Kpi
                icone="mdi:shield-check-outline"
                rotulo="Risco de ban"
                valor={dashboard.kpis.risco_ban.rotulo}
              />
            </div>

            <div className="mob__cartao">
              <strong style={{ fontSize: 13.5, fontWeight: 500 }}>Containers</strong>
              {dashboard.containers.map((c) => (
                <div key={c.servico} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Ponto kind={kindDoContainer(c.status)} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>{c.nome}</span>
                  <span style={{ fontSize: 11, color: 'var(--sga-fg-3)' }}>
                    {c.versao ?? '—'} · {n(c.msg_hora)}/h
                  </span>
                  <Tag kind={kindDoContainer(c.status)}>{c.status}</Tag>
                </div>
              ))}
            </div>

            <div className="mob__cartao">
              <strong style={{ fontSize: 13.5, fontWeight: 500 }}>Sistemas integrados</strong>
              {dashboard.integracoes.map((s) => (
                <div key={s.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Ponto kind={s.integrado ? 'ok' : 'mut'} />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.sistema}
                    </span>
                    <Tag kind={s.integrado ? 'ok' : 'mut'}>
                      {s.integrado ? 'Integrado' : 'Não'}
                    </Tag>
                  </div>
                  <div style={{ margin: '5px 0 3px' }}>
                    <Barra pct={s.progresso_pct} altura={5} kind={s.integrado ? 'ok' : 'mut'} />
                  </div>
                  <div style={{ display: 'flex', fontSize: 11, color: 'var(--sga-fg-3)' }}>
                    <span>
                      {s.metrica ?? 'eventos'} · {n(s.volume)}
                    </span>
                    <span style={{ flex: 1 }} />
                    <span className="mono">{s.latencia_p95_ms ? `${s.latencia_p95_ms} ms` : '—'}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mob__cartao">
              <strong style={{ fontSize: 13.5, fontWeight: 500 }}>Incidentes</strong>
              {dashboard.incidentes.length === 0 && (
                <span className="hint">Nada nas últimas horas.</span>
              )}
              {dashboard.incidentes.map((i) => (
                <div key={i.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Ponto kind={kindDoNivel(i.nivel)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5 }}>{i.evento}</div>
                    <div style={{ fontSize: 11, color: 'var(--sga-fg-3)' }}>
                      {i.origem} · {i.codigo ?? '—'}
                    </div>
                  </div>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--sga-fg-3)' }}>
                    {hhmm(i.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {aba === 'conexoes' && (
          <>
            <div className="input-group">
              <input
                className="input"
                placeholder="Buscar conexão..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
              <span className="addon">
                <Icon icon="mdi:magnify" width={16} />
              </span>
            </div>

            <div className="mob__chips">
              {CHIPS.map((c) => (
                <button
                  key={c.valor}
                  type="button"
                  className="mob__chip"
                  aria-pressed={filtro === c.valor}
                  onClick={() => setFiltro(c.valor)}
                >
                  {c.rotulo}
                </button>
              ))}
            </div>

            {filtradas.length === 0 && (
              <div className="sga-vazio">Nenhuma conexão neste filtro.</div>
            )}

            {filtradas.map((c) => {
              const kind = kindDoStatus(c.status);
              const canal = iconeDoCanal[c.canal];
              const maior = Math.max(1, ...conexoes.map((x) => x.na_fila));
              return (
                <button
                  key={c.id}
                  type="button"
                  className="mob__cartao"
                  style={{ textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => aoAbrirConexao(c.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      className="sga-avatar-canal"
                      style={{ width: 38, height: 38, background: 'var(--sga-gray-05)' }}
                    >
                      <Icon icon={canal.icone} width={19} style={{ color: canal.cor }} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5 }}>{c.nome}</div>
                      <div className="mono" style={{ fontSize: 11.5, color: 'var(--sga-fg-3)' }}>
                        {identidadeDaConexao(c.canal, c.numero)}
                      </div>
                    </div>
                    <Icon icon="mdi:chevron-right" width={18} style={{ color: 'var(--sga-fg-3)' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <TagComPonto kind={kind}>{rotuloStatus[c.status]}</TagComPonto>
                    <div style={{ flex: 1 }}>
                      <Barra pct={(c.na_fila / maior) * 100} kind={kind} />
                    </div>
                    <span style={{ fontSize: 11.5, color: 'var(--sga-fg-3)' }}>
                      {n(c.na_fila)} na fila
                    </span>
                  </div>
                </button>
              );
            })}

            <button type="button" className="mob__fab" onClick={aoNovaConexao}>
              <Icon icon="tdesign:file-add" width={18} />
              Nova conexão
            </button>
          </>
        )}

        {aba === 'filas' && antiban && (
          <>
            <Segmented
              largo
              valor={visaoFila}
              onChange={setVisaoFila}
              opcoes={[
                { valor: 'timeline', rotulo: 'Timeline' },
                { valor: 'grafico', rotulo: 'Gráfico' },
                { valor: 'throttling', rotulo: 'Throttling' },
              ]}
            />

            <div className="mob__cartao">
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--sga-fg-2)' }}>Risco de banimento</span>
                <span style={{ flex: 1 }} />
                <strong style={{ fontSize: 20, fontWeight: 400 }}>{antiban.resumo.risco_rotulo}</strong>
              </div>
              <div className="sga-medidor">
                <div
                  style={{
                    width: `${antiban.resumo.risco_pct}%`,
                    height: '100%',
                    borderRadius: 999,
                    background:
                      antiban.resumo.risco_pct >= 66
                        ? 'var(--sga-error)'
                        : antiban.resumo.risco_pct >= 33
                          ? 'var(--sga-warning)'
                          : 'var(--sga-tag-text)',
                  }}
                />
              </div>
              {fila && (
                <div className="sga-grid sga-grid--2" style={{ marginTop: 6 }}>
                  <Kpi icone="mdi:timer-sand" rotulo="Aguardando" valor={n(fila.resumo.aguardando)} />
                  <Kpi
                    icone="mdi:shield-alert-outline"
                    rotulo="Retidas"
                    valor={n(fila.resumo.retidas)}
                    cor="var(--sga-warning)"
                  />
                  <Kpi
                    icone="mdi:speedometer-slow"
                    rotulo="Throughput"
                    valor={`${n(fila.resumo.throughput_hora)}/h`}
                  />
                  <Kpi
                    icone="mdi:close-octagon-outline"
                    rotulo="Falhas 24h"
                    valor={n(fila.resumo.falhas_24h)}
                  />
                </div>
              )}
            </div>

            {visaoFila === 'timeline' &&
              (fila?.itens ?? []).slice(0, 30).map((i) => (
                <div className="mob__cartao" key={i.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="mono" style={{ fontSize: 11.5, color: 'var(--sga-fg-3)' }}>
                      {hhmmss(i.hora)}
                    </span>
                    <span style={{ flex: 1 }} />
                    <Tag kind={kindDoEstado(i.estado)}>{rotuloEstado[i.estado]}</Tag>
                  </div>
                  <div style={{ fontSize: 13 }}>{i.titulo}</div>
                  <div style={{ fontSize: 11, color: 'var(--sga-fg-3)' }}>
                    {i.conexao} · {telefone(i.destino)} · {i.origem}
                  </div>
                  {i.antiban_reason && i.delay_ms > 0 && (
                    <div className="sga-timeline__antiban" style={{ marginTop: 0 }}>
                      <Icon icon="mdi:timer-sand" width={14} />
                      Anti-Ban: {segundos(i.delay_ms)} de atraso
                    </div>
                  )}
                </div>
              ))}

            {visaoFila === 'grafico' && serie && (
              <div className="mob__cartao">
                <div className="sga-legenda">
                  <span>
                    <i style={{ background: 'var(--sga-primary)' }} />
                    Enviadas
                  </span>
                  <span>
                    <i style={{ background: 'var(--sga-warning)' }} />
                    Retidas
                  </span>
                </div>
                <div className="sga-grafico" style={{ height: 150 }}>
                  {serie.serie.slice(-7).map((p) => {
                    const pico = Math.max(1, ...serie.serie.map((x) => x.enviadas));
                    return (
                      <div className="sga-grafico__par" key={p.hora}>
                        <span
                          style={{
                            height: `${(p.enviadas / pico) * 100}%`,
                            background: 'var(--sga-primary)',
                          }}
                        />
                        <span
                          style={{
                            height: `${(p.retidas / pico) * 100}%`,
                            background: 'var(--sga-warning)',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="sga-grafico__eixo">
                  {serie.serie.slice(-7).map((p) => (
                    <span key={p.hora}>{hhmm(p.hora)}</span>
                  ))}
                </div>
                <div className="sga-linha-metrica">
                  <span>Pico</span>
                  <span className="sga-linha-metrica__valor">{n(serie.resumo.pico)}</span>
                </div>
                <div className="sga-linha-metrica">
                  <span>Retidas</span>
                  <span className="sga-linha-metrica__valor">{pct(serie.resumo.retidas_pct)}</span>
                </div>
              </div>
            )}

            {visaoFila === 'throttling' &&
              antiban.throttling.map((l) => (
                <div className="mob__cartao" key={l.conexao_id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}>{l.nome}</span>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {l.taxa}
                    </span>
                  </div>
                  <Barra
                    pct={l.pct}
                    kind={l.estado === 'Limitando' ? 'warn' : l.estado === 'Banido' ? 'err' : 'ok'}
                  />
                  <div style={{ display: 'flex', fontSize: 11, color: 'var(--sga-fg-3)' }}>
                    <span>{l.detalhe}</span>
                    <span style={{ flex: 1 }} />
                    <span>fila {n(l.na_fila)}</span>
                  </div>
                </div>
              ))}
          </>
        )}

        {aba === 'logs' && (
          <>
            {logs.length === 0 && <div className="sga-vazio">Sem registros.</div>}
            {logs.slice(0, 60).map((r) => (
              <div className="mob__cartao" key={r.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag kind={kindDoNivel(r.nivel)}>{rotuloNivel[r.nivel]}</Tag>
                  <span style={{ flex: 1 }} />
                  <span className="mono" style={{ fontSize: 11.5, color: 'var(--sga-fg-3)' }}>
                    {hhmm(r.created_at)}
                  </span>
                </div>
                <div style={{ fontSize: 13 }}>{r.evento}</div>
                <div style={{ fontSize: 11, color: 'var(--sga-fg-3)' }}>
                  {r.origem} · {r.codigo ?? '—'}
                </div>
              </div>
            ))}
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--sga-fg-3)', padding: 8 }}>
              SGA Petro © 2011–2026
            </div>
          </>
        )}
      </div>

      <nav className="mob__tabbar">
        {ABAS.map((a) => (
          <button
            key={a.aba}
            type="button"
            className="mob__tab"
            aria-current={aba === a.aba ? 'page' : undefined}
            onClick={() => setAba(a.aba)}
          >
            <Icon icon={a.icone} width={20} />
            {a.rotulo}
          </button>
        ))}
      </nav>

      {folhaAberta && (
        <div className="mob__folha" onClick={() => setFolhaAberta(false)}>
          <div className="mob__folha-card" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '4px 6px 10px' }}>
              <strong style={{ fontSize: 14, fontWeight: 500 }}>Trocar de tenant</strong>
              <span style={{ flex: 1 }} />
              <Botao variante="link" onClick={() => setFolhaAberta(false)}>
                Fechar
              </Botao>
            </div>
            {tenants.map((t) => (
              <button
                key={t.id}
                type="button"
                className="sga-popover__item"
                aria-current={t.id === tenantAtivo?.id}
                onClick={() => escolherTenant(t)}
              >
                <Icon icon={t.icone} width={18} />
                <span style={{ flex: 1 }}>
                  {t.nome}
                  <div style={{ fontSize: 11, color: 'var(--sga-fg-3)' }}>{t.conexoes} conexões</div>
                </span>
                {t.id === tenantAtivo?.id && <Icon icon="mdi:check-bold" width={15} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
